import { el, on } from '../util/dom';
import { DeviceSettings, SettingsFormState } from './types';
import { getDeviceSettings, updateDeviceSettings, resetDeviceSettings } from './queries';
import { createSettingsForm } from './form';
import { SETTINGS_FORM_STYLES } from './styles';

export interface SettingsViewProps {
  familyId: string;
  deviceId: string;
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void;
}

export async function createSettingsView(props: SettingsViewProps): Promise<HTMLElement> {
  const container = el('div', { class: 'settings-view' });

  // Add styles
  injectSettingsStyles();

  // Header
  const header = el('div', { class: 'settings-header' });
  header.appendChild(el('h2', { class: 'settings-title' }, 'Device Settings'));
  container.appendChild(header);

  // Loading state
  const loading = el('div', { class: 'settings-loading' });
  loading.appendChild(el('div', { class: 'spinner' }, ''));
  loading.appendChild(el('div', {}, 'Loading settings...'));
  container.appendChild(loading);

  try {
    // Load settings
    const settings = await getDeviceSettings(props.familyId, props.deviceId);

    // Remove loading state
    loading.remove();

    if (!settings) {
      const error = el('div', { class: 'settings-error' }, 'Failed to load device settings');
      container.appendChild(error);
      props.onError?.('Settings not found');
      return container;
    }

    // Create form
    const form = createSettingsForm({
      settings,
      onSave: async (formSettings: SettingsFormState) => {
        try {
          await updateDeviceSettings(props.familyId, props.deviceId, {
            displayName: formSettings.displayName,
            restrictionMode: formSettings.restrictionMode,
            newAppPolicy: formSettings.newAppPolicy,
            newAppDefaultLimitMinutes: formSettings.newAppDefaultLimitMinutes,
            timezone: formSettings.timezone,
            notificationsEnabled: formSettings.notificationsEnabled,
            notificationTypes: {
              blockEvents: formSettings.notificationBlockEvents,
              limitReached: formSettings.notificationLimitReached,
              appInstalled: formSettings.notificationAppInstalled,
              scheduleChange: formSettings.notificationScheduleChange,
            },
          });

          // Show success message
          const success = el('div', { class: 'settings-success' }, '✅ Settings saved successfully');
          container.insertBefore(success, form);

          // Remove success message after 3 seconds
          setTimeout(() => success.remove(), 3000);

          props.onSuccess?.('Settings saved');
        } catch (err) {
          throw err;
        }
      },
      onReset: async () => {
        try {
          await resetDeviceSettings(props.familyId, props.deviceId);

          // Reload page to show updated settings
          location.reload();
        } catch (err) {
          throw err;
        }
      },
      onCancel: () => {
        // Clear form or close modal if in modal mode
        console.log('Settings cancelled');
      },
    });

    container.appendChild(form);
  } catch (err) {
    loading.remove();
    const errorDiv = el('div', { class: 'settings-error' }, `Error loading settings: ${(err as Error).message}`);
    container.appendChild(errorDiv);
    props.onError?.((err as Error).message);
  }

  return container;
}

// Helper: Inject styles
function injectSettingsStyles() {
  if (document.getElementById('settings-view-styles')) return;

  const style = el('style');
  style.id = 'settings-view-styles';
  style.textContent = SETTINGS_FORM_STYLES;
  document.head.appendChild(style);
}
